import React from "react";
import { Box, useMediaQuery } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import LaunchIcon from "@mui/icons-material/Launch";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import { dt, accentShimmer } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { OfferServiceRequest } from "../../models/models";
import dayjs from "dayjs";

interface OfferRfsContextCardProps {
  serviceRequest?: OfferServiceRequest;
}

const OfferRfsContextCard: React.FC<OfferRfsContextCardProps> = ({ serviceRequest }) => {
  const { translate } = useTranslation(["serviceOffers"]);
  const isPhone = useMediaQuery("(max-width:768px)");

  if (!serviceRequest) return null;

  const machine = serviceRequest.machine;
  const machineImage = machine?.imageUrl || machine?.catalogImageUrl;
  const locationText = [serviceRequest.city, serviceRequest.countryName].filter(Boolean).join(", ");

  return (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        border: `1px solid ${dt.cardBorder}`,
        borderRadius: dt.radius,
        overflow: "hidden",
      }}
    >
      {/* Accent bar */}
      <Box sx={{ height: "3px", background: accentShimmer }} />

      <Box sx={{ padding: "20px" }}>
        {/* Section header with link */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "14px" }}>
          <Box
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "1px",
              color: dt.textMuted,
              fontFamily: dt.font,
            }}
          >
            {translate("serviceRequestInfo")}
          </Box>
          {serviceRequest.id && (
            <Box
              component={RouterLink}
              to={`/service-board/${serviceRequest.id}`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "12px",
                fontWeight: 600,
                fontFamily: dt.font,
                color: dt.textLink,
                textDecoration: "none",
                transition: "opacity 0.15s",
                "&:hover": { opacity: 0.8 },
              }}
            >
              {serviceRequest.internalNumber}
              <LaunchIcon sx={{ fontSize: 13 }} />
            </Box>
          )}
        </Box>

        {/* Issue */}
        <Box
          sx={{
            fontSize: "16px",
            fontWeight: 700,
            fontFamily: dt.font,
            color: dt.textPrimary,
            mb: "8px",
            lineHeight: 1.4,
          }}
        >
          {serviceRequest.issue}
        </Box>

        {/* Description */}
        {serviceRequest.description && (
          <Box
            sx={{
              fontSize: "13px",
              fontFamily: dt.font,
              color: dt.textSecondary,
              lineHeight: 1.5,
              mb: "12px",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {serviceRequest.description}
          </Box>
        )}

        {/* Meta row: open date, location */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", mb: machine ? "14px" : 0 }}>
          {serviceRequest.openDate && (
            <Box sx={{ fontSize: "12px", fontFamily: dt.fontMono, color: dt.textMuted }}>
              {translate("offerDetail.openDate")}: {dayjs(serviceRequest.openDate).format("MMM D, YYYY")}
            </Box>
          )}
          {locationText && (
            <Box sx={{ fontSize: "12px", fontFamily: dt.fontMono, color: dt.textMuted }}>
              {locationText}
            </Box>
          )}
        </Box>

        {/* Machine mini-row */}
        {machine && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              padding: "10px 12px",
              backgroundColor: dt.headerBg,
              borderRadius: dt.radiusSm,
              border: `1px solid ${dt.rowBorder}`,
            }}
          >
            {machineImage ? (
              <Box
                component="img"
                src={machineImage}
                alt={machine.name}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: dt.radiusSm,
                  objectFit: "cover",
                  border: `1px solid ${dt.cardBorder}`,
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: dt.radiusSm,
                  backgroundColor: dt.inputBg,
                  border: `1px solid ${dt.cardBorder}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <PrecisionManufacturingOutlinedIcon sx={{ fontSize: 18, color: dt.textMuted }} />
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: dt.font,
                  color: dt.textPrimary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {machine.brandName ? `${machine.brandName} ${machine.name}` : machine.name}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OfferRfsContextCard;
